import { z } from 'zod'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'
import { PageQuery } from './pagination.ts'

/**
 * ══ HỢP ĐỒNG API — KẾ HOẠCH & MỤC TIÊU (F31) ═════════════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/az/ke-hoach` và `/api/v1/ncc/ke-hoach`.
 *
 * ⚠ CỬA CỦA THẦU PHỤ KHÔNG có `vendorId` — thầu phụ nào là do PHIÊN quyết, cùng luật với F13/F28.
 */

export const PlanScope = z.enum(['company', 'sale', 'vendor'])
export const PlanMetric = z.enum(['revenue', 'margin', 'trips'])

export const CreatePeriodBody = z.object({
  code: z.string().min(1, 'Chưa đặt mã kỳ.').max(32),
  name: z.string().min(1, 'Chưa đặt tên kỳ.'),
  fromDate: ISODate,
  toDate: ISODate,
  note: blankAsAbsent(z.string()),
})

export const PeriodQuery = z.object({ limit: PageQuery.shape.limit })
export const PeriodIdParams = z.object({ id: Uuid })
export const PeriodStepBody = z.object({ version: Version })

/**
 * ⚠ `targetValue` DÙNG `moneyField` cho CẢ chỉ tiêu SỐ CHUYẾN. Cột ở CSDL là `NUMERIC(20,2)` chứa
 *   được cả hai, và một lược đồ duy nhất là một chỗ duy nhất để kiểm — xem chú thích cột ở `0053`.
 */
export const SetTargetBody = z.object({
  periodId: Uuid,
  scope: PlanScope,
  scopeId: blankAsAbsent(Uuid),
  metric: PlanMetric,
  targetValue: moneyField('Mục tiêu'),
  note: blankAsAbsent(z.string()),
})

export const SetVendorPlanBody = z.object({
  periodId: Uuid,
  vendorId: Uuid,
  routeName: blankAsAbsent(z.string()),
  plannedTrips: z.number().int().min(0).max(100_000),
  note: blankAsAbsent(z.string()),
})

export const MyPlanQuery = z.object({ limit: PageQuery.shape.limit })

export type CreatePeriodBodyT = z.infer<typeof CreatePeriodBody>
export type PeriodQueryT = z.infer<typeof PeriodQuery>
export type SetTargetBodyT = z.infer<typeof SetTargetBody>
export type SetVendorPlanBodyT = z.infer<typeof SetVendorPlanBody>
export type MyPlanQueryT = z.infer<typeof MyPlanQuery>

/* ══ KH-BC TH ▸ NHẬP LIỆU — LƯỚI CHỈ TIÊU THEO CHIỀU ══════════════════════════════════════════ */

export const PlanDimEnum = z.enum(['pack', 'route', 'cont'])

/** Báo cáo KH-BC TH — `year` bắt buộc (không có cửa RT trần gọi cửa này). */
export const KhthReportQuery = z.object({
  year: z.coerce.number({ invalid_type_error: 'Năm phải là số.' }).int().min(2000).max(2100),
})
export type KhthReportQueryT = z.infer<typeof KhthReportQuery>

export const KhthGridQuery = z.object({
  year: z.coerce.number({ invalid_type_error: 'Năm phải là số.' }).int().min(2000).max(2100),
  dim: PlanDimEnum.default('pack'),
})

/**
 * ⚠ `keyLabel` GỬI KÈM, cố ý trùng lặp với danh mục: bản cũ chốt tên hiển thị NGAY LÚC NHẬP để
 *   báo cáo năm ngoái không đổi tên theo khi ai đó sửa danh mục. Xem migration `0058`.
 */
export const SetDimTargetBody = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  dim: PlanDimEnum,
  key: z.string().min(1, 'Thiếu khoá của dòng chỉ tiêu.'),
  keyLabel: z.string().default(''),
  targetValue: moneyField('Chỉ tiêu'),
  note: blankAsAbsent(z.string()),
})

export const SetPrevQtyBody = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  dim: PlanDimEnum,
  key: z.string().min(1, 'Thiếu khoá của dòng sản lượng.'),
  keyLabel: z.string().default(''),
  /** SỐ LƯỢNG đơn/cont — không phải tiền, nên không đi qua `moneyField`. */
  qty: z.number().int().min(0, 'Sản lượng không được âm.').max(1_000_000),
})

/** 📌 Ghi / gỡ Thực hiện nhập tay của MỘT ô — `amount` "0" = trả về tự tính (xoá). */
export const SetActualOverrideBody = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  dim: PlanDimEnum,
  key: z.string().min(1, 'Thiếu khoá của dòng thực hiện.'),
  keyLabel: z.string().default(''),
  amount: moneyField('Thực hiện'),
})

/** Nạp cả loạt từ tệp Excel — MỘT giao dịch. Trần 1.000 ô, xem `@rs/plan`. */
export const ImportDimTargetsBody = z.object({
  rows: z.array(SetDimTargetBody).min(1, 'Không có ô nào để nạp.').max(1000),
})

export const DimTargetIdParams = z.object({ id: Uuid })
/** Cửa xoá KHÔNG có thân — nhưng `POST` vẫn phải khai lược đồ, để thân lạ bị từ chối. */
export const KhthNoBody = z.object({}).strict()

export type KhthGridQueryT = z.infer<typeof KhthGridQuery>
export type SetDimTargetBodyT = z.infer<typeof SetDimTargetBody>
export type SetPrevQtyBodyT = z.infer<typeof SetPrevQtyBody>
export type SetActualOverrideBodyT = z.infer<typeof SetActualOverrideBody>
export type ImportDimTargetsBodyT = z.infer<typeof ImportDimTargetsBody>
